/**
 * 用户端漫剧业务 HTTP 客户端（ThinkPHP index/UserApi）。
 * 与 `docs/功能文档.md` 中各步骤接口一一对应；新增 action 请在 userApi 对象内按「认证 / 会员 / 项目 / 步骤1~4 / 资产 / 支付」分组并写简短 JSDoc。
 */
/** 未配置 VUE_APP_API_BASE 时的默认后端域名（可被 .env.* 覆盖） */
const DEFAULT_API_HOST = {
  development: "http://localhost:9994",
  production: "https://www.ailintu.com"
};

function resolveApiHost() {
  const fromEnv = String(process.env.VUE_APP_API_BASE || "").trim();
  if (fromEnv) return fromEnv;
  const nodeEnv = process.env.NODE_ENV === "production" ? "production" : "development";
  return DEFAULT_API_HOST[nodeEnv] || DEFAULT_API_HOST.development;
}

function normalizeApiBase(rawBase) {
  // 开发环境统一走同源代理，彻底规避 CORS（目标地址见 vue.config.js devServer.proxy）。
  if (process.env.NODE_ENV === "development") {
    return "/index.php?s=index/UserApi";
  }
  const base = String(rawBase || resolveApiHost()).replace(/\/+$/, "");
  // 兼容 ThinkPHP query 路由形式，例如：
  // http://localhost:9994/index.php?s=index/UserApi
  if (/\/index\.php\?s=/i.test(base)) {
    return base;
  }
  // 兼容两种配置：
  // 1) 仅配置主机：http://localhost:9994
  // 2) 配置完整前缀：http://localhost:9994/api/user 或 .../index.php/api/user
  if (/\/(?:index\.php\/)?api\/user$/i.test(base)) {
    return base;
  }
  return `${base}/index.php?s=index/UserApi`;
}

const API_BASE = normalizeApiBase(resolveApiHost());

function request(path, options = {}) {
  const token = localStorage.getItem("user_token") || "";
  const headers = Object.assign(
    {},
    options.headers || {},
    token ? { Authorization: token, "X-Auth-Token": token } : {}
  );
  let body;
  if (options.body !== undefined) {
    if (options.formData) {
      body = options.body;
      // 注意：FormData 不能手动设置 Content-Type（包含 boundary），让浏览器自动处理
    } else
    if (options.form) {
      headers["Content-Type"] = "application/x-www-form-urlencoded;charset=UTF-8";
      body = new URLSearchParams(options.body).toString();
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
  }

  const action = String(path || "").replace(/^\/+/, "");
  let url;
  // ThinkPHP：`s` 中含多级路径时，部分网关/代理对未编码的 `/` 处理不一致，统一编码可避免 404
  const tpUserPrefix = "index/UserApi";
  if (
    /\/index\.php\?s=index\/UserApi$/i.test(API_BASE) ||
    API_BASE.endsWith("?s=index/UserApi") ||
    API_BASE.endsWith("&s=index/UserApi")
  ) {
    const root = API_BASE.split("?")[0].split("&")[0] || "/index.php";
    url = `${root}?s=${encodeURIComponent(`${tpUserPrefix}/${action}`)}`;
  } else if (/\/index\.php$/i.test(API_BASE.replace(/[#].*$/, "")) && API_BASE.indexOf("UserApi") !== -1) {
    const root = API_BASE.indexOf("?") !== -1 ? API_BASE.slice(0, API_BASE.indexOf("?")) : API_BASE;
    url = `${root}?s=${encodeURIComponent(`${tpUserPrefix}/${action}`)}`;
  } else {
    url = `${API_BASE.replace(/\/+$/, "")}/${action}`;
  }
  const extraQuery = options.query && typeof options.query === "object" ? options.query : null;
  if (extraQuery) {
    Object.keys(extraQuery).forEach(key => {
      const v = extraQuery[key];
      if (v === undefined || v === null) return;
      url += (url.indexOf("?") === -1 ? "?" : "&") + encodeURIComponent(key) + "=" + encodeURIComponent(String(v));
    });
  }
  return fetch(url, {
    method: options.method || "GET",
    headers,
    body
  })
    .then(async res => {
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        const snippet = String(text || "").slice(0, 160);
        throw new Error(`接口返回非 JSON（HTTP ${res.status}）：${snippet}`);
      }
      return data;
    })
    .then(res => {
      if (res.code !== 0) {
        const err = new Error(res.msg || "请求失败");
        err.apiCode = res.code;
        if (res.bizCode) err.bizCode = res.bizCode;
        throw err;
      }
      return res.data;
    });
}

/**
 * 前端请求层优化：
 *  1. 同 URL+body 的并发请求做 in-flight 去重，避免 hydrateProjectFromApi + resumeStep2ImageTasksFromServer
 *     + refreshStep2EntityPreviewsFromServer 同一时刻发 3 个 projectDetail；
 *  2. GET 类只读接口按方法 + URL + body 做短期 TTL 缓存（如字典/模型/资产库 30s）。
 * 实现策略：在 request() 外再包一层 cachedRequest，业务方可以选择性使用；userApi 各方法按需调用。
 */

/** @type {Map<string, Promise<any>>} 同 key 在飞请求合并 */
const inflightCache = new Map();
/** @type {Map<string, {exp:number, value:any}>} 只读 GET 缓存 */
const ttlCache = new Map();

function buildRequestKey(method, path, body, query) {
  const m = String(method || "GET").toUpperCase();
  const p = String(path || "").replace(/^\/+/, "");
  // body/query 序列化（统一排序保证命中）
  const norm = (v) => {
    if (v == null) return v;
    if (typeof v !== "object") return String(v);
    if (Array.isArray(v)) return v.map(norm);
    const keys = Object.keys(v).sort();
    const out = {};
    for (const k of keys) out[k] = norm(v[k]);
    return out;
  };
  const b = norm(body) || "";
  const q = norm(query) || "";
  return m + "|" + p + "|" + JSON.stringify(b) + "|" + JSON.stringify(q);
}

function requestCached(method, path, options = {}, { ttlMs = 0, dedup = true, noCache = false } = {}) {
  const upper = String(options.method || method || "GET").toUpperCase();
  const useTtl = ttlMs > 0 && upper === "GET" && !noCache;
  const useDedup = dedup !== false;
  const key = buildRequestKey(upper, path, options.body, options.query);
  const now = Date.now();
  if (useTtl) {
    const hit = ttlCache.get(key);
    if (hit && hit.exp > now) {
      return Promise.resolve(hit.value);
    }
    if (hit) ttlCache.delete(key);
  }
  if (noCache && upper === "GET") {
    // 强制刷新：拼一个时间戳 query 绕过任何潜在反向代理/CDN 缓存
    const sep = path.indexOf("?") >= 0 ? "&" : "?";
    path = path + sep + "_t=" + now;
  }
  if (useDedup) {
    const inflight = inflightCache.get(key);
    if (inflight) return inflight;
  }
  const p = request(path, options).then(
    (val) => {
      if (useTtl) ttlCache.set(key, { exp: now + ttlMs, value: val });
      return val;
    },
    (err) => {
      // 失败不算 inflight 缓存，但允许下次重试
      throw err;
    }
  ).finally(() => {
    if (useDedup) inflightCache.delete(key);
  });
  if (useDedup) inflightCache.set(key, p);
  return p;
}

/** 清空全部前端请求缓存（路由切换 / 退出登录时调用） */
function clearRequestCache() {
  inflightCache.clear();
  ttlCache.clear();
}

export const userApi = {
  /** 无需登录：服务探活（DB、任务表最近更新时间、Redis 可选） */
  health() {
    return request("health");
  },
  login(payload) {
    return request("login", { method: "POST", body: payload, form: true });
  },
  sendLoginCode(payload) {
    return request("sendLoginCode", { method: "POST", body: payload, form: true });
  },
  profile() {
    return request("profile");
  },
  /** 无需登录：可积分购买的会员套餐（price_points>0） */
  vipPlans() {
    return requestCached("GET", "vipPlans", {}, { ttlMs: 60 * 1000 });
  },
  /** 积分购买会员（需登录） body: { planId } */
  buyVipPlan(body) {
    return request("buyVipPlan", { method: "POST", body, form: true });
  },
  /** 微信/支付宝购买会员（需登录） body: { vipPlanId, payType } */
  buyVipPlanPay(body) {
    return request("buyVipPlanPay", { method: "POST", body, form: true });
  },
  projects() {
    return request("projects");
  },
  deleteProject(id) {
    return request("deleteProject", { method: "POST", body: { id }, form: true });
  },
  createProject(body) {
    return request("createProject", { method: "POST", body, form: true });
  },
  updateProject(body) {
    return request("updateProject", { method: "POST", body, form: true });
  },
  /** 轻量：仅更新项目 meta_json（不要求 title/ratio/type） */
  updateProjectMetaJson(body) {
    return request("updateProjectMetaJson", { method: "POST", body, form: true });
  },
  /** 步骤一草稿保存（meta_json） */
  saveProjectDraft(body) {
    return request("saveProjectDraft", { method: "POST", body, form: true });
  },
  /** 单个项目详情（含 meta 草稿） */
  projectDetail(id) {
    // GET 类，10s TTL + 同 id 并发去重（hydrateProjectFromApi / refreshStep2EntityPreviewsFromServer
    // / resumeStep2ImageTasksFromServer 同一时刻可能并发触发，复用同一次响应）
    return requestCached("GET", "projectDetail", { query: { id } }, { ttlMs: 10 * 1000 });
  },
  assets() {
    return requestCached("GET", "assets", {}, { ttlMs: 30 * 1000 });
  },
  uploadAsset({ file, assetType, name }) {
    const form = new FormData();
    form.append("file", file);
    if (assetType) form.append("assetType", assetType);
    if (name) form.append("name", name);
    return request("uploadAsset", { method: "POST", body: form, formData: true });
  },
  updateAsset(payload) {
    return request("updateAsset", { method: "POST", body: payload, form: true });
  },
  deleteAsset(id) {
    return request("deleteAsset", { method: "POST", body: { id }, form: true });
  },
  /** 将项目实体添加到资产库（步骤二资源→步骤四九宫格引用） */
  addEntityToAssetLibrary(entityId) {
    return request("addEntityToAssetLibrary", {
      method: "POST",
      body: { entityId },
      form: true
    });
  },
  /** 将资产库图片同步到 SD2 官转素材库，写入 volc_asset_id */
  syncAssetVolc(id, waitActive = true) {
    return request("syncAssetVolc", {
      method: "POST",
      body: { id, waitActive: waitActive ? 1 : 0 },
      form: true
    });
  },
  /** 支付订单（`fa_ai_recharge_order`）分页；query: { offset, limit, type: all|recharge|vip } */
  orders(query = {}) {
    const q = {};
    if (query.offset != null) q.offset = query.offset;
    if (query.limit != null) q.limit = query.limit;
    if (query.type) q.type = query.type;
    return Object.keys(q).length ? request("orders", { query: q }) : request("orders");
  },
  /** 积分流水；query: { offset, limit }，返回 { records, hasMore, nextOffset, ... } */
  pointsRecords(query = {}) {
    const q = {};
    if (query.offset != null) q.offset = query.offset;
    if (query.limit != null) q.limit = query.limit;
    return Object.keys(q).length ? request("pointsRecords", { query: q }) : request("pointsRecords");
  },
  /** 无需登录 */
  rechargePlans() {
    return requestCached("GET", "rechargePlans", {}, { ttlMs: 60 * 1000 });
  },
  /** 无需登录：画幅、类型、画面风格 */
  setupDictionary() {
    return requestCached("GET", "setupDictionary", {}, { ttlMs: 5 * 60 * 1000 });
  },
  /** 无需登录：视频生成模型（时长/分辨率来自后台 fa_ai_model） */
  videoModels(query, opts) {
    const noCache = !!(opts && opts.noCache);
    return requestCached("GET", "videoModels", { query: query || {} }, { ttlMs: 5 * 60 * 1000, noCache });
  },
  /** 无需登录：文字模型（fa_ai_model.model_type=text），步骤一 */
  textModels() {
    return requestCached("GET", "textModels", {}, { ttlMs: 5 * 60 * 1000 });
  },
  /** 无需登录：图片模型（fa_ai_model.model_type=image），步骤二 */
  imageModels() {
    return requestCached("GET", "imageModels", {}, { ttlMs: 5 * 60 * 1000 });
  },
  /** 步骤一：异步任务创建 */
  createGenerateProjectStep1Task(body) {
    return request("createGenerateProjectStep1Task", { method: "POST", body, form: true });
  },
  /** 步骤一：失败任务直接重试（不重复扣分；同一 taskId 直接重新入队） */
  retryStep1Task(body) {
    return request("retryStep1Task", { method: "POST", body, form: true });
  },
  /** 步骤一：仅重新生成单集剧本 + 分镜（重新扣分；不影响其他集） */
  regenerateProjectStep1Episode(body) {
    return request("regenerateProjectStep1Episode", { method: "POST", body, form: true });
  },
  /** 步骤一：恢复页面上「进行中 / 刚完成」的 regen 任务（用于刷新后恢复右下角进度） */
  listMyStep1RegenerateEpisodeTasks(query) {
    return request("listMyStep1RegenerateEpisodeTasks", { method: "GET", query });
  },
  /** 步骤一：单独刷新子步骤 1~5（**同步入口已停用**，后端返回 410）
   *  请改用 createGenerateProjectStep1Task 提交 + getGenerateProjectStep1TaskStatus 轮询。
   *  仅保留 stub 以避免破坏 import；新代码禁止调用。*/
  refreshProjectStep1SubStep(body) {
    return request("refreshProjectStep1SubStep", { method: "POST", body, form: true });
  },
  /** 清空步骤一～三落库数据，保留剧本正文，用于重新生成前重置 */
  resetProjectForStep1Regenerate(body) {
    return request("resetProjectForStep1Regenerate", { method: "POST", body, form: true });
  },
  /** 步骤一：异步任务轮询 */
  getGenerateProjectStep1TaskStatus(body) {
    return request("getGenerateProjectStep1TaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤三：读取分集列表 + 分镜列表 + 分集绑定实体（**重命名后的接口**）。
   *  原 generateProjectStep3Storyboards 名实不符（实际是读 SELECT），已重命名。
   *  body: { id, episodeId?, backfillCovers?, enableBackfill? } */
  listProjectStoryboards(body) {
    return request("listProjectStoryboards", { method: "POST", body, form: true });
  },
  /** 步骤三旧名（已重命名 → 后端返回 410），保留 stub 仅作 deprecation 提示，禁止新代码调用 */
  generateProjectStep3Storyboards(body) {
    return request("generateProjectStep3Storyboards", { method: "POST", body, form: true });
  },
  /** 步骤二：创建实体预览图异步任务（需 worker 消费） */
  generateEntityImage(body) {
    return request("generateEntityImage", { method: "POST", body, form: true });
  },
  /** 步骤二：轮询实体预览图任务状态 */
  getGenerateEntityImageTaskStatus(body) {
    return request("getGenerateEntityImageTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤二：批量轮询实体预览图任务状态（一次请求合并 N 个 taskId，减小服务器压力） */
  batchGetGenerateEntityImageTaskStatus(body) {
    return request("batchGetGenerateEntityImageTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤二：批量入队实体预览图任务（一次提交多个 entityId，取代 N 次串行 generateEntityImage） */
  batchGenerateEntityImages(body) {
    return request("batchGenerateEntityImages", { method: "POST", body, form: true });
  },
  /** 步骤四：单条分镜「扩充描述」—调用后端把过短 prompt 充实为 10~15s 的密度文本 */
  expandShotPrompt(body) {
    return request("expandShotPrompt", { method: "POST", body, form: true });
  },
  /**
   * 步骤四：扩充描述（异步，推荐）—— 立即返回 taskId，前端轮询 getExpandShotPromptTaskStatus
   * 与下方旧同步 expandShotPrompt 的参数完全一致。
   */
  createExpandShotPromptTask(body) {
    return request("createExpandShotPromptTask", { method: "POST", body, form: true });
  },
  /** 步骤四：查询异步扩充描述任务状态 */
  getExpandShotPromptTaskStatus(body) {
    return request("getExpandShotPromptTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤二轻量接口：仅返回当前项目的实体预览（无 step1/episode/shot），用于频繁刷新 */
  getProjectEntities(body) {
    return request("getProjectEntities", { method: "POST", body, form: true });
  },
  /** 步骤四/quick：批量轮询视频生成任务状态 */
  batchGetGenerateStoryboardVideoTaskStatus(body) {
    return request("batchGetGenerateStoryboardVideoTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤二：当前项目未完成的实体预览任务（pending/running），刷新后恢复队列 */
  listStep2EntityImageTasksForProject(body) {
    return request("listStep2EntityImageTasksForProject", { method: "POST", body, form: true });
  },
  /** 步骤二：轻量保存单个实体（避免大 metaJson） */
  saveProjectEntity(body) {
    return request("saveProjectEntity", { method: "POST", body, form: true });
  },
  /** 步骤四：生成/重新生成单个分镜视频片段（同步接口，**已停用返回 410）
   *  请改用 createGenerateStoryboardVideoTask + getGenerateStoryboardVideoTaskStatus（异步任务）。
   *  保留仅为历史兼容，禁止新代码调用。*/
  generateStoryboardVideo(body) {
    return request("generateStoryboardVideo", { method: "POST", body, form: true });
  },
  /** 步骤四：创建异步视频生成任务 */
  createGenerateStoryboardVideoTask(body) {
    return request("createGenerateStoryboardVideoTask", { method: "POST", body, form: true });
  },
  /** 步骤四：批量提交全部分镜视频任务（按 shots 粒度逐条入队；已存在 pending/running 跳过） */
  batchCreateStoryboardVideoTasks(body) {
    return request("batchCreateStoryboardVideoTasks", { method: "POST", body, form: true });
  },
  /** 步骤四：保存当前分镜提示词 */
  saveStoryboardPrompt(body) {
    return request("saveStoryboardPrompt", { method: "POST", body, form: true });
  },
  /** 步骤四：保存当前分镜视频参数（时长/分辨率） */
  saveStoryboardVideoConfig(body) {
    return request("saveStoryboardVideoConfig", { method: "POST", body, form: true });
  },
  /** 步骤四：查询异步视频生成任务状态 */
  getGenerateStoryboardVideoTaskStatus(body) {
    return request("getGenerateStoryboardVideoTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤四：列出项目下进行中的视频生成任务 */
  listStep4VideoTasks(body) {
    return request("listStep4VideoTasks", { method: "POST", body, form: true });
  },
  /** 视频快速创作：创建任务 */
  createQuickVideoTask(body) {
    return request("createQuickVideoTask", { method: "POST", body, form: true });
  },
  /** 视频快速创作：轮询状态 */
  getQuickVideoTaskStatus(body) {
    return request("getQuickVideoTaskStatus", { method: "POST", body, form: true });
  },
  /** 视频快速创作：历史列表 */
  listQuickVideoJobs(body) {
    return request("listQuickVideoJobs", { method: "POST", body, form: true });
  },
  /** 分镜页：将资产库图片绑定到分集 */
  bindEpisodeAssets(body) {
    return request("bindEpisodeAssets", { method: "POST", body, form: true });
  },
  /** 分镜页：将本项目实体（fa_ai_project_entity）绑定到分集 */
  bindEpisodeProjectEntities(body) {
    return request("bindEpisodeProjectEntities", { method: "POST", body, form: true });
  },
  /** 双链路·剧本阶段：按当前剧本正文 AI 预解析并写入分集-实体预绑定（auto_script），可与分镜 @ 校准并存 */
  scriptDramaParseBindings(body) {
    return request("scriptDramaParseBindings", { method: "POST", body, form: true });
  },
  /** body: { planId } 或 { points } */
  recharge(body) {
    return request("recharge", { method: "POST", body, form: true });
  },
  queryRechargeOrder(body) {
    return request("queryRechargeOrder", { method: "POST", body, form: true });
  },
  rechargePayNotify(body) {
    return request("rechargePayNotify", { method: "POST", body, form: true });
  }
};

/** 清空前端请求 in-flight 合并 + TTL 缓存（退出登录时调用） */
userApi.clearRequestCache = clearRequestCache;
